import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Header */}
          <div className="mb-12">
            <p className="data-label mb-2">Volatility-Adjusted Alpha</p>
            <h1 className="text-4xl font-bold text-[#111111] tracking-tight">
              Trading Bot
            </h1>
            <p className="mt-4 text-[#555555]">
              Multi-timeframe crypto trading with dynamic risk management.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="data-label block mb-2">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono"
              />
            </div>

            <div>
              <label className="data-label block mb-2">Password</label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#111111]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-primary flex items-center justify-center gap-2 font-medium disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-[#555555]">
            Don't have an account?{" "}
            <Link
              to="/register"
              data-testid="register-link"
              className="text-[#002FA7] hover:underline font-medium"
            >
              Create Account
            </Link>
          </p>

          {/* Features */}
          <div className="mt-12 pt-8 border-t border-[#111111]/10">
            <p className="data-label mb-4">Features</p>
            <ul className="space-y-2 text-sm text-[#555555]">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#002FA7]"></span>
                4H EMA Trend + 30m Entry Triggers
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#002FA7]"></span>
                Dynamic Risk & Leverage Engine
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#002FA7]"></span>
                Hybrid Profit-Taking System
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#002FA7]"></span>
                Binance & Bybit Support
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/8cb06352-fd18-4514-9e3b-904961bc2f5f/images/d8429818e078eae1d74078ad481dc946f534508be7b88fa493e610b33df8441d.png)`,
        }}
      >
        <div className="h-full w-full bg-[#002FA7]/5 backdrop-blur-[1px]"></div>
      </div>
    </div>
  );
}
